import {
  NOTIFICATION_READ_BATCH_DELAY_MS,
  NOTIFICATION_READ_BATCH_MAX_WAIT_MS,
  NOTIFICATION_READ_RETRY_DELAY_MS,
} from '../../../assets/services/notifications';

const queueByService = new WeakMap();

function createQueueState() {
  return {
    maxWaitTimer: null,
    orderedIds: [],
    pendingIds: new Set(),
    quietTimer: null,
    requestQueue: Promise.resolve(),
    retryTimer: null,
  };
}

function getQueueState(notificationService) {
  let state = queueByService.get(notificationService);

  if (!state) {
    state = createQueueState();
    queueByService.set(notificationService, state);
  }

  return state;
}

function mergeNotificationOrder(currentIds, notifications) {
  const nextIds = (notifications || [])
    .map((notification) => notification.id)
    .filter(Boolean);
  const nextIdSet = new Set(nextIds);

  currentIds.forEach((notificationId) => {
    if (!nextIdSet.has(notificationId)) {
      nextIds.push(notificationId);
    }
  });

  return nextIds;
}

function getPendingRange(state, pendingIds = state.pendingIds) {
  const targetIndexes = state.orderedIds.reduce((indexes, notificationId, index) => {
    if (pendingIds.has(notificationId)) {
      indexes.push(index);
    }

    return indexes;
  }, []);

  if (!targetIndexes.length) return null;

  const startIndex = Math.min(...targetIndexes);
  const endIndex = Math.max(...targetIndexes);

  return {
    startId: state.orderedIds[startIndex],
    endId: state.orderedIds[endIndex],
  };
}

function clearBatchTimers(state) {
  window.clearTimeout(state.quietTimer);
  window.clearTimeout(state.maxWaitTimer);
  state.quietTimer = null;
  state.maxWaitTimer = null;
}

function clearRetryTimer(state) {
  window.clearTimeout(state.retryTimer);
  state.retryTimer = null;
}

function markRangeAsSeen(notificationService, range) {
  if (notificationService.markRangeAsSeen) {
    return notificationService.markRangeAsSeen(range);
  }

  return notificationService.markAsSeen(range.startId);
}

function scheduleRetry(notificationService, state) {
  if (state.retryTimer !== null) return;

  state.retryTimer = window.setTimeout(() => {
    state.retryTimer = null;
    flushNotificationReadQueue(notificationService);
  }, NOTIFICATION_READ_RETRY_DELAY_MS);
}

function scheduleFlush(notificationService, state) {
  window.clearTimeout(state.quietTimer);
  state.quietTimer = window.setTimeout(() => {
    flushNotificationReadQueue(notificationService);
  }, NOTIFICATION_READ_BATCH_DELAY_MS);

  if (state.maxWaitTimer === null) {
    state.maxWaitTimer = window.setTimeout(() => {
      flushNotificationReadQueue(notificationService);
    }, NOTIFICATION_READ_BATCH_MAX_WAIT_MS);
  }
}

export function flushNotificationReadQueue(notificationService) {
  const state = getQueueState(notificationService);

  if (!state.pendingIds.size) {
    clearBatchTimers(state);
    return;
  }

  clearBatchTimers(state);
  clearRetryTimer(state);

  const requestIds = new Set(state.pendingIds);
  const range = getPendingRange(state, requestIds);
  state.pendingIds.clear();

  if (!range) return;

  state.requestQueue = state.requestQueue
    .catch(() => undefined)
    .then(() => markRangeAsSeen(notificationService, range))
    .catch(() => {
      requestIds.forEach((notificationId) => state.pendingIds.add(notificationId));
      scheduleRetry(notificationService, state);
    });
}

export function enqueueNotificationRead(notificationService, notificationId, notifications) {
  const state = getQueueState(notificationService);
  state.orderedIds = mergeNotificationOrder(state.orderedIds, notifications);
  state.pendingIds.add(notificationId);
  scheduleFlush(notificationService, state);

  return getPendingRange(state);
}

export function discardPendingNotificationReads(notificationService) {
  const state = getQueueState(notificationService);
  state.pendingIds.clear();
  clearBatchTimers(state);
  clearRetryTimer(state);
}
